/**
 * 投稿詳細画面の「対応内容」表示（読み取り専用）。
 * 仕様書: docs/02_work/done/plan_board_feature.md §7-7
 *
 * 宛先者がアクション時に入力した URL / リッチテキストと、その人が添付した
 * 成果物ファイルを **1 人ずつまとめて** 本文の下に表示する。
 * 保存後は入力欄を出しっぱなしにせず、自分の対応内容カードの「編集」ボタンで
 * 入力フォーム（親の「あなたのアクション」直下）へ表示を切り替える。
 */
import type { BoardAttachmentView } from '@goenhub/shared';
import { BOARD_RECIPIENT_STATE_LABEL } from '@goenhub/shared';
import { Download, ExternalLink, Lock, Paperclip, Pencil } from 'lucide-react';
import { useState } from 'react';

import { BOARD_STATE_VARIANT } from '../boardMeta';
import { AttachmentPasswordDialog } from './AttachmentPasswordDialog';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { useBoardActionReports } from '@/hooks/useBoardActionReports';
import {
  formatBytes,
  startDownload,
  useBoardAttachments,
  useResolveAttachmentDownload,
} from '@/hooks/useBoardAttachments';

interface Props {
  postId: string;
  /** ログイン中の uid。自分のカードにだけ「編集」ボタンを出す。 */
  myUid?: string | null;
  /** 編集できるか（宛先者 && 未クローズ）。false なら閲覧のみ。 */
  canEdit?: boolean;
  /** 「編集」押下。親が入力フォームを開く。 */
  onEdit?: () => void;
  /** 編集中は自分のカードを隠す（入力フォームへ表示を切り替えるため）。 */
  editing?: boolean;
}

export function BoardSubmissionList({ postId, myUid, canEdit, onEdit, editing }: Props) {
  const reports = useBoardActionReports(postId);
  const attachments = useBoardAttachments(postId);
  const resolveDownload = useResolveAttachmentDownload(postId);

  // 保護付きファイルのパスワード入力（入力と照合は AttachmentPasswordDialog が持つ）
  const [pwTarget, setPwTarget] = useState<BoardAttachmentView | null>(null);

  const reportList = reports.data ?? [];
  const deliverables = (attachments.data ?? []).filter((a) => a.kind !== 'reference');

  // 記入者・アップロード者の uid で束ねる（同じ人の入力と成果物を並べて見せる）
  // 編集中の自分は入力フォーム側に出ているので、ここからは外す。
  const uids = Array.from(
    new Set([...reportList.map((r) => r.uid), ...deliverables.map((a) => a.uploaderUid)]),
  ).filter((u) => !(editing && u === myUid));

  // 表示するものが無ければセクションごと出さない
  if (uids.length === 0) return null;

  const handleDownload = async (a: BoardAttachmentView) => {
    if (a.protection === 'password') {
      setPwTarget(a);
      return;
    }
    try {
      const res = await resolveDownload.mutateAsync({ attachmentId: a.id });
      if (res.status === 'ok') startDownload(res.downloadUrl);
      else if (res.status === 'file_missing') toast.error('ファイルが見つかりません');
      else toast.error('ダウンロードできませんでした');
    } catch (err) {
      toast.error(`ダウンロード失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 space-y-3">
      <span className="text-xs font-medium text-[var(--text-secondary)]">対応内容</span>

      {uids.map((uid) => {
        const r = reportList.find((x) => x.uid === uid);
        const files = deliverables.filter((a) => a.uploaderUid === uid);
        const name = r?.uploaderName ?? files[0]?.uploaderName ?? uid;

        return (
          <div key={uid} className="rounded-md border border-[var(--border)] p-2.5 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-[var(--text-primary)]">{name}</span>
              {r && (
                <Badge variant={BOARD_STATE_VARIANT[r.state]}>
                  {BOARD_RECIPIENT_STATE_LABEL[r.state]}
                </Badge>
              )}
              {/* 自分の対応内容だけ編集できる（サーバも uid 一致を強制）。
                  r が無い＝成果物だけの人は、入力フォームが元から出ているので不要。 */}
              {canEdit && onEdit && myUid && uid === myUid && r && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="ml-auto"
                  onClick={onEdit}
                >
                  <Pencil className="size-3.5" />
                  編集
                </Button>
              )}
            </div>

            {/* URL */}
            {r?.url && (
              <a
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-[var(--accent)] hover:underline break-all"
              >
                <ExternalLink className="size-3.5 flex-shrink-0" />
                {r.url}
              </a>
            )}

            {/* リッチテキスト（サーバで sanitizeBoardHtml 済み） */}
            {r?.bodyHtml && (
              <div
                className="board-body prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: r.bodyHtml }}
              />
            )}

            {/* 成果物ファイル */}
            {files.length > 0 && (
              <div className="space-y-1.5 pt-0.5">
                {files.map((a) => (
                  <div
                    key={a.id}
                    className="rounded border border-[var(--border)] p-2 flex items-center gap-2"
                  >
                    <Paperclip className="size-3.5 text-[var(--text-secondary)] flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[var(--text-primary)] truncate flex items-center gap-1.5">
                        {a.protection === 'password' && (
                          <Lock className="size-3.5 text-[var(--accent-amber)] flex-shrink-0" />
                        )}
                        {a.fileName}
                      </div>
                      <div className="text-[11px] text-[var(--text-muted)]">
                        {formatBytes(a.size)}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => void handleDownload(a)}
                      disabled={resolveDownload.isPending}
                    >
                      <Download className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* パスワード入力（保護付きファイル） */}
      <AttachmentPasswordDialog
        postId={postId}
        target={pwTarget}
        onClose={() => setPwTarget(null)}
        inputId="submission-password"
      />
    </div>
  );
}
